import { useEffect, useMemo, useRef, useState } from 'react';
import {
  DEMAND_LIST_COLUMNS,
  buildListRows,
  cellValue,
  loadListSort,
  loadVisibleColumnIds,
  saveListSort,
  sortFieldLabel,
} from '../lib/demandListColumns.js';
import { nextColumnSort, sortStatusLabel } from '../lib/listColumnSort.js';
import { selectedMattersFromIds } from '../lib/bulkDemandActions.js';
import { debtorDisplayName } from '../lib/debtorIdentity.js';
import ListPagination, { useListPagination } from './ListPagination.jsx';
import ListSortMark from './ListSortMark.jsx';

function BulkActionTip({ label, text }) {
  return (
    <span className="list-bulk-tip kanban-stage-tip">
      <button
        type="button"
        className="kanban-stage-tip-btn"
        tabIndex={0}
        aria-label={`${label}: ${text}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden>
          <circle cx="8" cy="8" r="6.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
          <path
            d="M8 7.2v3.6M8 5.2h.01"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </button>
      <span className="kanban-stage-tip-bubble list-bulk-tip-bubble" role="tooltip">
        {text}
      </span>
    </span>
  );
}

const BULK_ACTION_TIPS = {
  export: 'Download the selected demands as a CSV spreadsheet.',
  syncConnected:
    'Sync selected demands that have invoice IDs to your connected accounting app and mark those invoices as paid.',
  syncDisconnected:
    'Connect an accounting app in Settings to sync selected demands and mark invoices as paid.',
  archive: 'Move selected demands into Archive. View and restore them anytime from Archived.',
  delete:
    'Permanently delete selected archived demands. This cannot be undone. Files stored in Evidence Locker are kept.',
};

export default function MattersTable({
  matters,
  onOpenMatter,
  visibleIds: visibleIdsProp,
  onBulkExport,
  onBulkSyncAccounting,
  onBulkArchive,
  onBulkUnarchive,
  onBulkDelete,
  accountingConnected = false,
  mode = 'active',
}) {
  const [fallbackVisibleIds] = useState(() => loadVisibleColumnIds());
  const visibleIds = visibleIdsProp || fallbackVisibleIds;
  const [sort, setSort] = useState(() => loadListSort());
  const [selected, setSelected] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const bulkMenuRef = useRef(null);
  const scrollRef = useRef(null);

  const visibleColumns = useMemo(
    () => DEMAND_LIST_COLUMNS.filter((col) => visibleIds.includes(col.id)),
    [visibleIds]
  );

  const rows = useMemo(() => buildListRows(matters, sort), [matters, sort]);
  const pagination = useListPagination(rows, {
    storageKey: mode === 'archived' ? 'demands-archived' : 'demands',
  });
  const pageRows = pagination.pageItems;
  const sortLabel = sortStatusLabel(sort, sortFieldLabel);
  const selectedRows = useMemo(
    () => selectedMattersFromIds(matters, selected),
    [matters, selected]
  );

  useEffect(() => {
    setSelected(new Set());
  }, [pagination.page, pagination.pageSize, mode]);

  useEffect(() => {
    if (!selected.size) setBulkOpen(false);
  }, [selected.size]);

  useEffect(() => {
    if (!bulkOpen) return undefined;
    function onDocClick(e) {
      if (bulkMenuRef.current && !bulkMenuRef.current.contains(e.target)) {
        setBulkOpen(false);
      }
    }
    function onKey(e) {
      if (e.key === 'Escape') setBulkOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [bulkOpen]);

  function runBulkAction(action) {
    const rowsSelected = selectedRows;
    setBulkOpen(false);
    action?.(rowsSelected);
    if (action !== onBulkExport) {
      setSelected(new Set());
    }
  }

  function toggleHeaderSort(columnId) {
    const col = DEMAND_LIST_COLUMNS.find((c) => c.id === columnId);
    const sortType =
      columnId === 'date' ? 'date' : col?.sortType || 'string';
    setSort(saveListSort(nextColumnSort(sort, columnId, sortType)));
  }

  function toggleSelect(id, checked) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  function toggleSelectAll(checked) {
    if (!checked) {
      setSelected(new Set());
      return;
    }
    setSelected(new Set(pageRows.map((row) => String(row.matter.ROWID))));
  }

  const pageIds = pageRows.map((row) => String(row.matter.ROWID));
  const selectedOnPage = pageIds.filter((id) => selected.has(id)).length;
  const allSelected = pageIds.length > 0 && selectedOnPage === pageIds.length;
  const someSelected = selectedOnPage > 0 && selectedOnPage < pageIds.length;

  if (!matters.length) {
    return (
      <div className="empty-state">
        {mode === 'archived' ? 'No archived demands yet.' : 'No demands yet.'}
      </div>
    );
  }

  return (
    <div className="list-view">
      <div className="list-view-toolbar">
        <p className="list-view-count">
          {matters.length} demand{matters.length === 1 ? '' : 's'}
          {selected.size ? ` · ${selected.size} selected` : ''}
          {sortLabel ? ` · ${sortLabel}` : ''}
        </p>
        {selected.size ? (
          <div className="list-bulk-menu" ref={bulkMenuRef}>
            <button
              type="button"
              className="btn-ghost list-bulk-toggle"
              aria-expanded={bulkOpen}
              aria-haspopup="menu"
              onClick={() => setBulkOpen((open) => !open)}
            >
              Bulk Actions
              <span aria-hidden>▾</span>
            </button>
            {bulkOpen ? (
              <div className="list-bulk-dropdown" role="menu" aria-label="Bulk demand actions">
                {mode === 'archived' ? (
                  <>
                    <div className="list-bulk-item-row" role="none">
                      <button
                        type="button"
                        role="menuitem"
                        className="list-bulk-item"
                        onClick={() => runBulkAction(onBulkUnarchive)}
                      >
                        Unarchive
                      </button>
                      <BulkActionTip
                        label="Unarchive"
                        text="Restore selected demands to the active Demands list."
                      />
                    </div>
                    <div className="list-bulk-item-row" role="none">
                      <button
                        type="button"
                        role="menuitem"
                        className="list-bulk-item is-danger"
                        onClick={() => runBulkAction(onBulkDelete)}
                      >
                        Delete
                      </button>
                      <BulkActionTip label="Delete" text={BULK_ACTION_TIPS.delete} />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="list-bulk-item-row" role="none">
                      <button
                        type="button"
                        role="menuitem"
                        className="list-bulk-item"
                        onClick={() => runBulkAction(onBulkExport)}
                      >
                        Export
                      </button>
                      <BulkActionTip label="Export" text={BULK_ACTION_TIPS.export} />
                    </div>
                    <div className="list-bulk-item-row" role="none">
                      <button
                        type="button"
                        role="menuitem"
                        className="list-bulk-item"
                        disabled={!accountingConnected}
                        onClick={() => runBulkAction(onBulkSyncAccounting)}
                      >
                        Sync to accounting
                      </button>
                      <BulkActionTip
                        label="Sync to accounting"
                        text={
                          accountingConnected
                            ? BULK_ACTION_TIPS.syncConnected
                            : BULK_ACTION_TIPS.syncDisconnected
                        }
                      />
                    </div>
                    <div className="list-bulk-item-row" role="none">
                      <button
                        type="button"
                        role="menuitem"
                        className="list-bulk-item is-danger"
                        onClick={() => runBulkAction(onBulkArchive)}
                      >
                        Archive
                      </button>
                      <BulkActionTip label="Archive" text={BULK_ACTION_TIPS.archive} />
                    </div>
                  </>
                )}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="list-view-scroll" ref={scrollRef}>
        <table className="matters-table list-view-table">
          <thead>
            <tr>
              <th className="list-col-check">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected;
                  }}
                  onChange={(e) => toggleSelectAll(e.target.checked)}
                  aria-label="Select all demands"
                />
              </th>
              <th className="list-col-date">
                <button
                  type="button"
                  className={`list-sort-header${sort.columnId === 'date' ? ' is-active' : ''}`}
                  onClick={() => toggleHeaderSort('date')}
                  aria-label={`Sort by date ${
                    sort.columnId === 'date' && sort.direction === 'asc'
                      ? 'descending'
                      : 'ascending'
                  }`}
                >
                  Date
                  <ListSortMark active={sort.columnId === 'date'} direction={sort.direction} />
                </button>
              </th>
              {visibleColumns.map((col) => {
                const active = sort.columnId === col.id;
                return (
                  <th key={col.id} className={col.id === 'debtorName' ? 'list-col-primary' : undefined}>
                    <button
                      type="button"
                      className={`list-sort-header${active ? ' is-active' : ''}`}
                      onClick={() => toggleHeaderSort(col.id)}
                      aria-label={`Sort by ${col.label} ${
                        active && sort.direction === 'asc' ? 'descending' : 'ascending'
                      }`}
                    >
                      <span className="list-col-label">{col.label}</span>
                      <ListSortMark active={active} direction={sort.direction} />
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row) => {
              const matter = row.matter;
              const id = String(matter.ROWID);
              const isSelected = selected.has(id);
              return (
                <tr
                  key={id}
                  className={`matter-row list-view-row is-clickable${
                    isSelected ? ' is-selected' : ''
                  }`}
                  onClick={() => onOpenMatter?.(matter)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onOpenMatter?.(matter);
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label={`Open demand for ${debtorDisplayName(matter) || 'debtor'}`}
                >
                    <td
                      className="list-col-check"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => toggleSelect(id, e.target.checked)}
                        aria-label={`Select ${debtorDisplayName(matter) || 'demand'}`}
                      />
                    </td>
                    <td className="list-col-date">
                      {row.showDateTag ? (
                        <span className="list-date-tag">
                          <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden>
                            <circle
                              cx="8"
                              cy="8"
                              r="6"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.4"
                            />
                            <path
                              d="M8 4.5v3.5l2 1.5"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="1.4"
                              strokeLinecap="round"
                            />
                          </svg>
                          {row.dateLabel}
                        </span>
                      ) : null}
                    </td>
                    {visibleColumns.map((col) => {
                      const value = cellValue(matter, col.id);
                      const isEmail = col.id === 'email' && matter.Debtor_Email;
                      const isPrimary = col.id === 'debtorName';
                      return (
                        <td key={col.id} className={isPrimary ? 'list-col-primary' : undefined}>
                          {isEmail ? (
                            <a
                              className="list-column-link"
                              href={`mailto:${matter.Debtor_Email}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {value}
                            </a>
                          ) : isPrimary ? (
                            <strong className="list-column-link">{value}</strong>
                          ) : (
                            <span className="list-cell-text">{value}</span>
                          )}
                        </td>
                      );
                    })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ListPagination
        page={pagination.page}
        pageCount={pagination.pageCount}
        pageSize={pagination.pageSize}
        onPageSizeChange={pagination.setPageSize}
        rangeStart={pagination.rangeStart}
        rangeEnd={pagination.rangeEnd}
        canPrev={pagination.canPrev}
        canNext={pagination.canNext}
        onPrev={pagination.goPrev}
        onNext={pagination.goNext}
      />
    </div>
  );
}
